import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Row, Col, Input, Label, Table, Badge } from "reactstrap";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";

import apiClient from "../../axiosInstance";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import showAlert from "../../components/Common/ShowAlert";
import Swal from "sweetalert2";
import logo from "../../assets/images/logo.png";

const ExpensesList = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Expenses", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    expense_type_id: "",
  });

  const fetchExpenses = async (activeFilters = filters) => {
    try {
      const params = {};

      if (activeFilters.from_date) params.from_date = activeFilters.from_date;
      if (activeFilters.to_date) params.to_date = activeFilters.to_date;
      if (activeFilters.expense_type_id) {
        params.expense_type_id = activeFilters.expense_type_id;
      }

      const result = await apiClient.get("/expense", { params });
      setData(result.data.response || []);
      setExpandedGroups({});
    } catch (err) {
      console.error("Error fetching expenses:", err);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      const result = await apiClient.get("/expense/types");
      setExpenseTypes(result.data.response || []);
    } catch (err) {
      console.error("Error fetching expense types:", err);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchExpenseTypes();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    fetchExpenses(filters);
  };

  const resetFilters = () => {
    const resetState = {
      from_date: "",
      to_date: "",
      expense_type_id: "",
    };
    setFilters(resetState);
    fetchExpenses(resetState);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Expense",
      text: "Are you sure you want to delete this expense?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await apiClient.delete(`/expense/${id}`);
      await showAlert({
        title: "Expense",
        text: res.data.message || "Deleted",
        icon: res.data.status || "success",
      });
      fetchExpenses(filters);
    } catch (err) {
      console.error("Error deleting expense:", err);
      await showAlert({
        title: "Expense",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to delete expense",
        icon: "error",
      });
    }
  };

  const handlePrint = () => {
    if (!data.length) {
      showAlert({
        title: "Expenses",
        text: "No data available to print",
        icon: "warning",
      });
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const normalizeAmount = (value) => {
      const numeric = Number(
        String(value ?? 0)
          .replace(/[^0-9.-]/g, "")
          .trim()
      );
      return Number.isNaN(numeric) ? 0 : numeric;
    };

    const formatAmount = (value) =>
      normalizeAmount(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const totalAmount = data.reduce((sum, item) => {
      return sum + normalizeAmount(item.amount);
    }, 0);

    const reportRows = data
      .map(
        (item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(item.expense_type_name || "")}</td>
            <td class="amount-cell">${formatAmount(item.amount)}</td>
            <td>${item.expense_date ? format(parseISO(item.expense_date), "yyyy-MM-dd") : ""}</td>
            <td>${escapeHtml(item.branch_name || "")}</td>
            <td>${escapeHtml(item.description || "")}</td>
            <td>${escapeHtml(item.created_by_name || "")}</td>
          </tr>
        `
      )
      .join("");

    const selectedCategory =
      expenseTypes.find((item) => String(item.id) === String(filters.expense_type_id))
        ?.name || "All Categories";

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      showAlert({
        title: "Expenses",
        text: "Unable to open print window. Please allow popups and try again.",
        icon: "error",
      });
      return;
    }

    const generatedAt = new Date().toLocaleString();

    const printContent = `
      <html>
        <head>
          <title>Expenses Report</title>
          <style>
            @page { margin: 18mm 12mm; }
            body { font-family: "Segoe UI", Arial, sans-serif; color: #1f2937; margin: 0; }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 12px;
              margin-bottom: 14px;
            }
            .header-left { display: flex; align-items: center; gap: 12px; }
            .logo { width: 56px; height: 56px; object-fit: contain; }
            .title-wrap h1 { margin: 0; font-size: 20px; color: #111827; }
            .title-wrap p { margin: 2px 0 0; font-size: 12px; color: #6b7280; }
            .meta {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 6px 16px;
              margin-bottom: 14px;
              font-size: 12px;
            }
            .meta-item strong { color: #111827; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th {
              background: #f3f4f6;
              color: #111827;
              border: 1px solid #d1d5db;
              padding: 8px;
              text-align: left;
            }
            td {
              border: 1px solid #e5e7eb;
              padding: 7px 8px;
              vertical-align: top;
            }
            .amount-cell { text-align: right; white-space: nowrap; }
            tfoot td {
              background: #f9fafb;
              font-weight: 700;
              border-top: 2px solid #9ca3af;
            }
            .footer {
              margin-top: 12px;
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              color: #6b7280;
              border-top: 1px solid #e5e7eb;
              padding-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-left">
              <img src="${logo}" alt="Daryeel Logo" class="logo" />
              <div class="title-wrap">
                <h1>Daryeel</h1>
                <p>Expenses Report</p>
              </div>
            </div>
            <div style="font-size:12px;color:#6b7280;">Generated: ${escapeHtml(generatedAt)}</div>
          </div>

          <div class="meta">
            <div class="meta-item"><strong>From:</strong> ${escapeHtml(filters.from_date || "-")}</div>
            <div class="meta-item"><strong>To:</strong> ${escapeHtml(filters.to_date || "-")}</div>
            <div class="meta-item"><strong>Category:</strong> ${escapeHtml(selectedCategory)}</div>
            <div class="meta-item"><strong>Total Records:</strong> ${data.length}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Branch</th>
                <th>Description</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              ${reportRows}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2">Total</td>
                <td class="amount-cell">${formatAmount(totalAmount)}</td>
                <td colspan="4"></td>
              </tr>
            </tfoot>
          </table>

          <div class="footer">
            <span>Daryeel - Expenses Module</span>
            <span>Printed report</span>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const normalizeAmount = (value) => {
    const numeric = Number(
      String(value ?? 0)
        .replace(/[^0-9.-]/g, "")
        .trim()
    );
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const formatAmount = (value) => {
    return normalizeAmount(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const groupedExpenses = useMemo(() => {
    const groupsMap = new Map();

    data.forEach((item) => {
      const groupKey = `${item.expense_type_id || ""}`;

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, {
          group_key: groupKey,
          expense_type_name: item.expense_type_name,
          branches: new Set(),
          installments: 0,
          total_amount: 0,
          records: [],
        });
      }

      const group = groupsMap.get(groupKey);
      group.branches.add(item.branch_name || "-");
      group.records.push(item);
      group.installments += 1;
      group.total_amount += normalizeAmount(item.amount);
    });

    const groups = [];
    let groupNo = 1;

    groupsMap.forEach((group) => {
      groups.push({
        group_no: groupNo,
        group_key: group.group_key,
        expense_type_name: group.expense_type_name,
        branch_name:
          group.branches.size === 1
            ? [...group.branches][0]
            : "Multiple Branches",
        installments: group.installments,
        total_amount: group.total_amount,
        records: group.records,
      });

      groupNo += 1;
    });

    return groups;
  }, [data]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const formatExpenseDate = (dateValue) => {
    if (!dateValue) return "";
    try {
      return format(parseISO(dateValue), "yyyy-MM-dd");
    } catch {
      return dateValue;
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItems={breadcrumbItems} />

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Filters</h5>
            </div>
            <Row className="g-3 align-items-end">
              <Col md="3">
                <Label className="form-label">From Date</Label>
                <Input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="3">
                <Label className="form-label">To Date</Label>
                <Input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <Label className="form-label">Expense Category</Label>
                <Input
                  type="select"
                  name="expense_type_id"
                  value={filters.expense_type_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All Categories</option>
                  {expenseTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md="2">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-grow-1"
                    onClick={applyFilters}
                  >
                    Filter
                  </button>
                  <button
                    type="button"
                    className="btn btn-light border flex-grow-1"
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-success flex-grow-1"
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Expenses Table</h5>
              <Link to="/createExpense">
                <button className="btn btn-primary">Create Expense</button>
              </Link>
            </div>

            <div className="table-responsive">
              <Table className="table table-hover align-middle table-nowrap mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}></th>
                    <th>No</th>
                    <th>Expense Category</th>
                    <th>Installments</th>
                    <th>Total Installed</th>
                    <th>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedExpenses.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No expenses found
                      </td>
                    </tr>
                  ) : (
                    groupedExpenses.map((group) => {
                      const isExpanded = !!expandedGroups[group.group_key];

                      return (
                        <React.Fragment key={group.group_key}>
                          <tr className="table-light">
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-light border"
                                onClick={() => toggleGroup(group.group_key)}
                              >
                                <i
                                  className={
                                    isExpanded
                                      ? "ri-arrow-down-s-line"
                                      : "ri-arrow-right-s-line"
                                  }
                                ></i>
                              </button>
                            </td>
                            <td>{group.group_no}</td>
                            <td>
                              <strong>{group.expense_type_name}</strong>
                            </td>
                            <td>
                              <Badge color="info">{group.installments}</Badge>
                            </td>
                            <td>
                              <strong>{formatAmount(group.total_amount)}</strong>
                            </td>
                            <td>{group.branch_name}</td>
                          </tr>

                          {isExpanded && (
                            <tr>
                              <td colSpan="6" className="p-0">
                                <div className="p-3 bg-white border-top border-bottom">
                                  <div className="table-responsive">
                                    <Table className="table table-sm table-bordered table-striped mb-0">
                                      <thead>
                                        <tr>
                                          <th style={{ width: "60px" }}>No</th>
                                          <th>Date</th>
                                          <th>Amount</th>
                                          <th>Branch</th>
                                          <th>Description</th>
                                          <th>Created By</th>
                                          <th style={{ width: "120px" }}>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.records.map((record, index) => (
                                          <tr key={record.id}>
                                            <td>{index + 1}</td>
                                            <td>{formatExpenseDate(record.expense_date)}</td>
                                            <td>{formatAmount(record.amount)}</td>
                                            <td>{record.branch_name}</td>
                                            <td>{record.description || "-"}</td>
                                            <td>{record.created_by_name || "-"}</td>
                                            <td>
                                              <div className="d-flex gap-2">
                                                <Link
                                                  to={`/expenses/${record.id}`}
                                                  className="btn btn-sm btn-primary"
                                                >
                                                  <i className="ri-file-edit-line"></i>
                                                </Link>
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-danger"
                                                  onClick={() => handleDelete(record.id)}
                                                >
                                                  <i className="ri-delete-bin-line"></i>
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ExpensesList;
